import { createTheme } from '@mui/material/styles';
import '@fontsource/ubuntu/300.css';
import '@fontsource/ubuntu/400.css';
import '@fontsource/ubuntu/500.css';
import '@fontsource/ubuntu/700.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import { 
  colors as designColors, 
  typography as designTypography, 
  card as designCard, 
  muiThemeOverrides 
} from './design-tokens';

// ============================================================================
// TEMA DARK (ORIGINAL SÉTIMA - NEON)
// ============================================================================
const colors = {
  primary: {
    main: '#00F5B9', // Verde neon - cor principal da marca
    light: '#33F7C7',
    dark: '#00C294',
    contrastText: '#13171A',
  },
  secondary: {
    main: '#13171A', // Preto - fundo escuro
    light: '#2A3038',
    dark: '#0A0D0F',
    contrastText: '#F8F8F2',
  },
  background: {
    default: '#13171A', // Tema escuro por padrão
    paper: '#1E2329',
    light: '#F8F8F2', // Fundo claro alternativo
  },
  text: {
    primary: '#F8F8F2',
    secondary: '#A8ABB0',
    disabled: '#666A72',
  },
  success: {
    main: '#00F5B9',
    light: '#33F7C7',
    dark: '#00C294',
  },
  error: {
    main: '#FF4757',
    light: '#FF6B7A',
    dark: '#E63946',
  },
  warning: {
    main: '#FFA502',
    light: '#FFB733',
    dark: '#E68A00',
  },
  info: {
    main: '#3742FA',
    light: '#5F68FB',
    dark: '#2832C8',
  },
};

// Criar tema personalizado
const theme = createTheme({
  palette: {
    mode: 'dark',
    ...colors,
  },
  typography: {
    fontFamily: '"Ubuntu", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: 'Ubuntu',
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontFamily: 'Ubuntu',
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontFamily: 'Ubuntu',
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '0em',
    },
    h4: {
      fontFamily: 'Ubuntu',
      fontWeight: 500,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontFamily: 'Ubuntu',
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.5,
      letterSpacing: '0em',
    },
    h6: {
      fontFamily: 'Ubuntu',
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
    body1: {
      fontFamily: 'Ubuntu',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontFamily: 'Ubuntu',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    button: {
      fontFamily: 'Ubuntu',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
  },
  spacing: 8, // Sistema de espaçamento de 8px
  shape: {
    borderRadius: 20, // Border radius padrão dos cards
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30, // Botões com border radius maior
          padding: '12px 32px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 4px 12px rgba(0, 245, 185, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0, 245, 185, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          backgroundColor: '#1E2329',
          backgroundImage: 'none',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1E2329',
          border: '1px solid #00F5B9',
          fontSize: '0.875rem',
          padding: '12px 16px',
          borderRadius: 12,
        },
        arrow: {
          color: '#1E2329',
          '&::before': {
            border: '1px solid #00F5B9',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&.Mui-focused fieldset': {
              borderColor: '#00F5B9',
            },
          },
        },
      },
    },
  },
});

// ============================================================================
// TEMA LIGHT CORPORATIVO (NOVO - SPRINT UI FASE 1)
// ============================================================================

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    ...muiThemeOverrides.palette,
    // 🎨 CORREÇÃO DE CONTRASTE - WCAG AA (4.5:1 mínimo)
    text: {
      primary: designColors.gray[900],      // #0f172a - Contraste 12.6:1 ✅
      secondary: designColors.gray[700],    // #334155 - Contraste 9.8:1 ✅
      disabled: designColors.gray[400],     // #94a3b8 - Contraste 2.9:1 (apenas disabled)
    },
  },
  
  typography: {
    ...muiThemeOverrides.typography,
    fontFamily: designTypography.fontFamily.primary,
  },

  spacing: 8,
  
  shape: {
    borderRadius: 8,
  },

  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designCard.border.radius,
          border: `${designCard.border.width} solid ${designCard.border.color}`,
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          boxShadow: designCard.shadow.md,
          transition: designCard.hover.transition,
          '&:hover': {
            borderColor: designCard.hover.borderColor,
            boxShadow: designCard.hover.shadow,
            transform: designCard.hover.transform,
          },
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: designCard.padding.medium,
          '&:last-child': {
            paddingBottom: designCard.padding.medium,
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: designCard.padding.medium,
        },
        title: {
          // 🎨 CORREÇÃO: Títulos de cards legíveis
          color: designColors.gray[900],  // Contraste 12.6:1 ✅
          fontWeight: 700,
          fontSize: '1.125rem',
        },
        subheader: {
          // 🎨 CORREÇÃO CRÍTICA: Subheaders legíveis (era muito claro)
          color: designColors.gray[600],  // Contraste 7.2:1 ✅
          fontSize: '0.875rem',
          marginTop: '4px',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '10px 24px',
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          boxShadow: designCard.shadow.sm,
          '&:hover': {
            boxShadow: designCard.shadow.md,
            transform: 'translateY(-1px)',
          },
        },
        sizeLarge: {
          minHeight: '44px',
          padding: '14px 32px',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
        // 🎨 CORREÇÃO: Chips com cores legíveis
        colorSuccess: {
          backgroundColor: designColors.success.light,
          border: `1px solid ${designColors.success.dark}`,
          color: designColors.success.dark,  // Contraste adequado ✅
          fontWeight: 600,
        },
        colorError: {
          backgroundColor: designColors.error.light,
          border: `1px solid ${designColors.error.dark}`,
          color: designColors.error.dark,  // Contraste adequado ✅
          fontWeight: 600,
        },
        colorWarning: {
          backgroundColor: designColors.warning.light,
          border: `1px solid ${designColors.warning.dark}`,
          color: designColors.warning.dark,  // Contraste adequado ✅
          fontWeight: 600,
        },
        colorInfo: {
          backgroundColor: designColors.info.light,
          border: `1px solid ${designColors.info.dark}`,
          color: designColors.info.dark,  // Contraste adequado ✅
          fontWeight: 600,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: designColors.gray[800],
          fontSize: '0.875rem',
          padding: '8px 12px',
          borderRadius: 6,
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&.Mui-focused fieldset': {
              borderColor: designColors.brand[500],
              borderWidth: '2px',
            },
          },
          // 🎨 CORREÇÃO: Input values com contraste alto
          '& .MuiOutlinedInput-input': {
            color: designColors.gray[900],  // Contraste 12.6:1 ✅
            fontWeight: 600,  // Valores em destaque
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          // 🎨 CORREÇÃO CRÍTICA: Labels legíveis (era muito claro)
          color: designColors.gray[700],  // Contraste 9.8:1 ✅
          fontWeight: 500,
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: designColors.brand[600],
            fontWeight: 600,
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          // 🎨 CORREÇÃO: Descrições auxiliares legíveis
          color: designColors.gray[600],  // Contraste 7.2:1 ✅
          fontSize: '0.75rem',
          marginTop: '6px',
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          // 🎨 CORREÇÃO: Labels de form legíveis
          color: designColors.gray[700],  // Contraste 9.8:1 ✅
          fontWeight: 500,
          '&.Mui-focused': {
            color: designColors.brand[600],
          },
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          // Valores padrão já OK pelo palette.text
        },
        h1: {
          color: designColors.gray[900],  // Títulos principais
          fontWeight: 700,
        },
        h2: {
          color: designColors.gray[900],
          fontWeight: 700,
        },
        h3: {
          color: designColors.gray[900],
          fontWeight: 600,
        },
        h4: {
          color: designColors.gray[800],
          fontWeight: 600,
        },
        h5: {
          color: designColors.gray[800],
          fontWeight: 600,
        },
        h6: {
          color: designColors.gray[700],
          fontWeight: 600,
        },
        subtitle1: {
          // 🎨 CORREÇÃO: Subtítulos legíveis
          color: designColors.gray[700],  // Contraste 9.8:1 ✅
          fontWeight: 500,
        },
        subtitle2: {
          // 🎨 CORREÇÃO: Subtítulos menores legíveis
          color: designColors.gray[600],  // Contraste 7.2:1 ✅
          fontWeight: 500,
        },
        body1: {
          color: designColors.gray[700],  // Texto de corpo
        },
        body2: {
          color: designColors.gray[600],  // Texto secundário
        },
        caption: {
          // 🎨 CORREÇÃO: Captions legíveis (era muito claro)
          color: designColors.gray[600],  // Contraste 7.2:1 ✅
          fontSize: '0.75rem',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: designColors.gray[50],
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: designColors.gray[100],
          },
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: designColors.gray[100],
            borderBottom: `2px solid ${designColors.gray[300]}`,
          },
        },
      },
    },
  },
});

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

// Exporta tema dark como padrão (compatibilidade)
export default theme;

// Exporta tema dark explicitamente
export const darkTheme = theme;